import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * useScrollNavigation - Yatay kaydırma ve ok navigasyonu için hook
 * Drag-to-scroll, ok butonları ve visibility kontrolleri sağlar
 * 
 * @param {Object} options - Konfigürasyon seçenekleri
 * @param {number} options.scrollAmount - Her ok tıklamasında kaydırılacak piksel (varsayılan: 300)
 * @param {number} options.dragMultiplier - Sürükleme hız çarpanı (varsayılan: 1.5)
 * @returns {Object} - Scroll ref, state ve handler'lar
 */
export default function useScrollNavigation(options = {}) {
    const { scrollAmount = 300, dragMultiplier = 1.5 } = options;

    const scrollRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(true);

    // Ok görünürlüğünü güncelle
    const updateArrows = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        setShowLeftArrow(el.scrollLeft > 0);
        setShowRightArrow(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    }, []);

    // Drag başlatma
    const handleMouseDown = useCallback((e) => {
        if (!scrollRef.current) return;
        setIsDragging(true);
        setStartX(e.pageX - scrollRef.current.offsetLeft);
        setScrollLeft(scrollRef.current.scrollLeft);
    }, []);

    // Drag hareket
    const handleMouseMove = useCallback((e) => {
        if (!isDragging || !scrollRef.current) return;
        e.preventDefault();
        const x = e.pageX - scrollRef.current.offsetLeft;
        const walk = (x - startX) * dragMultiplier;
        scrollRef.current.scrollLeft = scrollLeft - walk;
        updateArrows();
    }, [isDragging, startX, scrollLeft, dragMultiplier, updateArrows]);

    // Drag bitirme
    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    // Yön butonları ile kaydırma
    const scrollToDirection = useCallback((direction) => {
        if (!scrollRef.current) return;
        const amount = direction === 'left' ? -scrollAmount : scrollAmount;
        scrollRef.current.scrollBy({ left: amount, behavior: 'smooth' });
    }, [scrollAmount]);

    // Scroll ve resize event listener'ları
    useEffect(() => {
        if (!scrollRef.current) return;

        const el = scrollRef.current;
        updateArrows();

        el.addEventListener('scroll', updateArrows);
        window.addEventListener('resize', updateArrows);

        return () => {
            el.removeEventListener('scroll', updateArrows);
            window.removeEventListener('resize', updateArrows);
        };
    }, [updateArrows]);

    // Scroll container için inline style
    const scrollContainerStyle = {
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        WebkitOverflowScrolling: 'touch',
    };

    return {
        scrollRef,
        isDragging,
        showLeftArrow,
        showRightArrow,
        scrollContainerStyle,
        handlers: {
            onMouseDown: handleMouseDown,
            onMouseMove: handleMouseMove,
            onMouseUp: handleMouseUp,
            onMouseLeave: handleMouseUp,
        },
        scrollToDirection,
        updateArrows,
    };
}
